/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { Breadcrumb, IBreadcrumbItem } from '@fluentui/react/lib/Breadcrumb';
import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { Stack } from '@fluentui/react/lib/Stack';
import { CustomizerContext } from '@fluentui/react/lib/Utilities';
import * as React from 'react';

import { FormsIframeSavingStatus } from '../../../models/formsIframe/FormsIframe';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { stringFormat } from '../../../utilities/Utils';
import {
    getBreadcrumbStyles,
    getInnerStackTokens,
    getStackStyles,
    getStatusStyles,
} from './CreateTemplateInstanceCommandBar.styles';

export interface CreateTemplateInstanceCommandBarComponentProps {
    navigationTitle: string;
    navigationSubTitle: string;
    onNavigateBack?: () => void;
    showNext: boolean;
    showBack: boolean;
    showPreview: boolean;
    showCancel: boolean;
    showSave: boolean;
    disableMainAction?: boolean;
    onNext?: () => void;
    onBack?: () => void;
    onPreview?: () => void;
    formsIframeSavingStatus?: FormsIframeSavingStatus;
}
export const CreateTemplateInstanceCommandBarComponent: React.FC<CreateTemplateInstanceCommandBarComponentProps> = (
    props: CreateTemplateInstanceCommandBarComponentProps
): JSX.Element => {
    const theme = React.useContext(CustomizerContext).customizations.settings.theme;
    const loc = getAppHost().getLocale();
    const {
        navigationTitle,
        navigationSubTitle,
        onNavigateBack,
        onNext,
        onBack,
        onPreview,
        disableMainAction,
        showBack,
        showPreview,
        showSave,
        formsIframeSavingStatus,
    } = props;

    const navItems = React.useMemo(
        (): IBreadcrumbItem[] => [
            { text: navigationTitle, key: 'templateManagement', onClick: onNavigateBack },
            { text: navigationSubTitle, key: 'createTemplateInstance' },
        ],
        [navigationTitle, navigationSubTitle, onNavigateBack]
    );

    let savingStatusText = '';
    if (formsIframeSavingStatus === FormsIframeSavingStatus.Failed) {
        savingStatusText = loc.strings.forms_save_failed;
    } else if (formsIframeSavingStatus === FormsIframeSavingStatus.Saving) {
        savingStatusText = loc.strings.forms_is_saving;
    }

    let secondaryButtonText = loc.strings.action_cancel;
    let secondaryButtonAriaLabel = loc.strings.action_cancel;
    let secondaryButtonOnClick = onNavigateBack;
    let secondaryButtonDisabled = false;
    if (showBack) {
        secondaryButtonText = loc.strings.carousel_back;
        secondaryButtonAriaLabel = loc.strings.carousel_back_aria_label;
        secondaryButtonOnClick = onBack;
        secondaryButtonDisabled = disableMainAction;
    }

    let primaryButtonText = loc.strings.step_next;
    let primaryButtonAriaLabel = loc.strings.step_next;
    let primaryButtonOnClick = onNext;
    if (showPreview) {
        primaryButtonText = loc.strings.preview_button_text;
        primaryButtonAriaLabel = loc.strings.preview_button_text;
        primaryButtonOnClick = onPreview;
    } else if (showSave) {
        primaryButtonText = loc.strings.save_button_text;
        primaryButtonAriaLabel = loc.strings.save_button_text;
        primaryButtonOnClick = onPreview;
    }

    return (
        <>
            <Stack horizontal verticalAlign='center' styles={getStackStyles(theme)}>
                <Stack.Item grow>
                    <Breadcrumb
                        ariaLabel={stringFormat(loc.strings.template_management_navigation_aria_label, {
                            subPath: navigationSubTitle,
                        })}
                        styles={getBreadcrumbStyles(theme)}
                        items={navItems}
                    />
                </Stack.Item>

                <Stack horizontal verticalAlign='center' tokens={getInnerStackTokens}>
                    <Stack.Item
                        styles={getStatusStyles(theme, formsIframeSavingStatus === FormsIframeSavingStatus.Failed)}>
                        <span
                            className='form-saving-status'
                            aria-label={savingStatusText}
                            role='status'
                            aria-live='polite'>
                            {savingStatusText}
                        </span>
                    </Stack.Item>

                    <DefaultButton
                        aria-label={secondaryButtonAriaLabel}
                        text={secondaryButtonText}
                        onClick={secondaryButtonOnClick}
                        disabled={secondaryButtonDisabled}
                    />

                    <PrimaryButton
                        aria-label={primaryButtonAriaLabel}
                        disabled={disableMainAction}
                        text={primaryButtonText}
                        onClick={primaryButtonOnClick}
                    />
                </Stack>
            </Stack>
        </>
    );
};
